import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import TabHeader from '../../Components/TabHeader/TabHeader';
import InputV1 from '../../../../components/ComponentsV1/InputV1';
import ActionForm from './Elements/ActionForm';
import { Star } from '../../../../UiKit/Star/Star';
import {
  useGetAADFeatures,
  useGetAADOperations,
  useGetAADComponents,
  useSubmitAADAction,
} from '../../apis';
import {
  FEATURE_PLACEHOLDER,
  OPERATION_PLACEHOLDER,
  DEFAULT_SUBMIT_SUCCESS_MESSAGE,
  NO_FORM_AVAILABLE_MESSAGE,
  MISSING_TARGET_ENDPOINT_MESSAGE,
} from './constants';
import {
  buildDropdownOptions,
  getValidFields,
  buildInitialValues,
  findMissingField,
  buildPayload,
} from './utils';

function AdminActionsDashboard() {
  const [selectedFeatureId, setSelectedFeatureId] = useState(null);
  const [selectedOperationId, setSelectedOperationId] = useState(null);
  const [activeComponent, setActiveComponent] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [fieldValuesVersion, setFieldValuesVersion] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useGetAADFeatures();

  const { data: operationsData, isLoading: isOperationsLoading } =
    useGetAADOperations();

  const { refetch: refetchComponents } = useGetAADComponents();

  const { mutate: submitAction, isLoading: isSubmitting } =
    useSubmitAADAction();

  const featureOptions = buildDropdownOptions(
    featuresData?.features,
    'id',
    'name',
  );

  const operationsForFeature = Array.isArray(operationsData?.features)
    ? operationsData.features.find(
        (feature) => feature?.feature_id === selectedFeatureId,
      )?.operations
    : [];
  const operationOptions = buildDropdownOptions(
    operationsForFeature,
    'id',
    'name',
  );

  const activeFields = getValidFields(activeComponent);

  const resetFieldValues = (component) => {
    setFieldValues(buildInitialValues(component));
    setFieldValuesVersion((prev) => prev + 1);
  };

  useEffect(() => {
    if (selectedFeatureId && selectedOperationId) {
      handleGetForm();
    }
  }, [selectedFeatureId, selectedOperationId]);

  const handleFeatureSelect = (value) => {
    setSelectedFeatureId(value);
    setSelectedOperationId(null);
    setActiveComponent(null);
    setFieldValues({});
    setShowConfirm(false);
  };

  const handleOperationSelect = (value) => {
    setSelectedOperationId(value);
    setActiveComponent(null);
    setFieldValues({});
    setShowConfirm(false);
  };

  const handleFieldChange = (name, value) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleGetForm = async () => {
    try {
      const { data } = await refetchComponents();
      const match = data?.components?.find(
        (item) =>
          item?.feature_id === selectedFeatureId &&
          item?.operation_id === selectedOperationId,
      );

      if (!match?.component) {
        toast.error(NO_FORM_AVAILABLE_MESSAGE);
      }

      setActiveComponent(match?.component ?? null);
      resetFieldValues(match?.component);
      setShowConfirm(false);
    } catch (error) {
      toast.error('Failed to load form. Please try again.');
    }
  };

  const performSubmit = () => {
    if (!activeComponent) return;

    const payload = buildPayload(fieldValues, activeComponent);

    submitAction(
      {
        targetEndpoint: activeComponent.target_endpoint,
        method: activeComponent.method || 'POST',
        data: payload,
      },
      {
        onSuccess: (data) => {
          toast.success(data?.message ?? DEFAULT_SUBMIT_SUCCESS_MESSAGE);
          resetFieldValues(activeComponent);
          setShowConfirm(false);
        },
      },
    );
  };

  const handleSubmit = () => {
    if (!activeComponent) return;

    const missingField = findMissingField(activeFields, fieldValues);
    if (missingField) {
      toast.error(`${missingField.label ?? missingField.name} is required`);
      return;
    }

    if (!activeComponent.target_endpoint) {
      toast.error(MISSING_TARGET_ENDPOINT_MESSAGE);
      return;
    }

    if (activeComponent.confirm_message && !showConfirm) {
      setShowConfirm(true);
      return;
    }

    performSubmit();
  };

  const handleCancelConfirm = () => setShowConfirm(false);

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-gap-5'>
      <TabHeader title='Admin Actions Dashboard' />

      <div className='tw-w-full tw-flex tw-gap-8 tw-bg-white tw-p-8 tw-rounded-2xl'>
        <div className='tw-flex tw-flex-col tw-gap-3 tw-w-1/4'>
          <div className='tw-font-medium tw-text-500'>
            Feature
            <Star icon='*' />
          </div>
          <InputV1
            loading={isFeaturesLoading}
            options={featureOptions}
            placeholder={FEATURE_PLACEHOLDER}
            multiple={false}
            isSearchable
            inputProps={{
              className: 'tw-border-none',
              type: 'text',
              style: { width: '100%' },
            }}
            style={{ width: '100%', display: 'block' }}
            closeOnSelect
            onSelect={(option) => handleFeatureSelect(option?.value)}
            value={selectedFeatureId}
          />
        </div>
        <div className='tw-flex tw-flex-col tw-gap-3 tw-w-1/4'>
          <div className='tw-font-medium tw-text-500'>
            Actions
            <Star icon='*' />
          </div>
          <InputV1
            loading={isOperationsLoading}
            options={operationOptions}
            placeholder={OPERATION_PLACEHOLDER}
            multiple={false}
            isSearchable
            disabled={!selectedFeatureId}
            inputProps={{
              className: 'tw-border-none',
              type: 'text',
              style: { width: '100%' },
            }}
            style={{ width: '100%', display: 'block' }}
            closeOnSelect
            onSelect={(option) => handleOperationSelect(option?.value)}
            value={selectedOperationId}
          />
        </div>
      </div>

      <ActionForm
        confirmMessage={activeComponent?.confirm_message}
        submitLabel={activeComponent?.submit_label}
        fields={activeFields}
        fieldValues={fieldValues}
        fieldValuesVersion={fieldValuesVersion}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        showConfirm={showConfirm}
        onConfirm={performSubmit}
        onCancelConfirm={handleCancelConfirm}
      />
    </div>
  );
}

export default AdminActionsDashboard;
